'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import {
  restaurantsClient,
  type RestaurantRecord,
  type RestaurantListResponse,
  type SearchRestaurantsParams,
} from '@bip/api-client';
import { RestaurantCard } from './RestaurantCard';
import { RestaurantCardSkeleton } from './Skeletons';

interface RestaurantsListProps {
  initialData: RestaurantListResponse;
}

/**
 * Coerce the string-shaped `params` bag (all values are query-string
 * strings) into the typed `SearchRestaurantsParams` shape the
 * `restaurantsClient.searchRestaurants` accepts. Numeric fields
 * (`page`, `limit`, `lat`, `lon`) are parsed; everything else is
 * passed through as-is. Empty / non-finite parsed numbers drop out.
 *
 * Callers previously built the URL by hand and hit
 * `/bip-api/restaurants/restaurants` directly. That relative path is
 * broken on any deployment where the browser-facing API prefix
 * differs from `bip-api` (e.g. test uses `bip-api-test`), because
 * Next.js only rewrites the env-driven prefix — the hardcoded one
 * falls through to whatever the edge/CDN routes `/bip-api/*` to on
 * that environment, which on test is a dead upstream (→ 502).
 * Routing through the shared client fixes this — the client hits
 * `NEXT_PUBLIC_RESTAURANTS_API_URL` directly.
 */
function toSearchParams(params: Record<string, string>): SearchRestaurantsParams {
  const out: SearchRestaurantsParams = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (key === 'page' || key === 'limit') {
      const n = parseInt(value, 10);
      if (Number.isFinite(n)) (out as any)[key] = n;
      continue;
    }
    if (key === 'lat' || key === 'lon') {
      const n = parseFloat(value);
      if (Number.isFinite(n)) (out as any)[key] = n;
      continue;
    }
    // `RestaurantsHero`'s "Find a table" submit writes the typed
    // restaurant-name query as `?q=` on the URL (matches the sports/
    // hotels convention), but `SearchRestaurantsDto` declares the field
    // as `search` — the backend's `ValidationPipe` runs
    // `forbidNonWhitelisted: true`, so forwarding the raw `q` key 400s
    // the request instead of scoring/filtering anything. Translate it.
    if (key === 'q') {
      out.search = value;
      continue;
    }
    (out as any)[key] = value;
  }
  return out;
}

async function fetchRestaurants(params: Record<string, string>): Promise<RestaurantListResponse> {
  return restaurantsClient.searchRestaurants(toSearchParams(params));
}

const USER_LAT_KEY = 'bip_user_lat';
const USER_LON_KEY = 'bip_user_lon';

/**
 * Read the currently-selected city from the cross-domain
 * `theatre-searches-city` blob. Written by the hero on submit and by
 * `commitCity` when the priority-1/2 resolver picks one. Returns an
 * empty string when the blob is missing / malformed — same shape
 * every consumer here treats as "no city".
 *
 * Kept as a plain sync read (not a React hook) so callers can read
 * ad-hoc inside event handlers.
 */
function readCityFromStorage(): string {
  if (typeof window === 'undefined') return '';
  try {
    const raw = localStorage.getItem('theatre-searches-city');
    if (!raw) return '';
    const parsed = JSON.parse(raw) as { city?: string } | null;
    return parsed?.city?.trim() || '';
  } catch {
    return '';
  }
}

export function RestaurantsList({ initialData }: RestaurantsListProps) {
  const searchParams = useSearchParams();
  const [restaurants, setRestaurants] = useState<RestaurantRecord[]>(initialData.data);
  const [page, setPage]         = useState(initialData.page);
  const [hasMore, setHasMore]   = useState(initialData.page < initialData.totalPages);
  const [isLoading, setIsLoading] = useState(false);
  // Mirrors Hotels' `searchError` — distinguishes "fetch failed" (retry
  // CTA) from "fetch succeeded with zero matches" (no retry, just a
  // softer empty-state message), instead of swallowing failures silently.
  const [searchError, setSearchError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [total, setTotal]       = useState(initialData.total);
  // City used to travel via `?city=` in the URL; per CMS spec the
  // listing URL stays clean now, so the value lives in local state
  // seeded from `theatre-searches-city` on mount and refreshed via
  // the `bipRestaurantsCityChanged` custom event the hero
  // broadcasts on commit / submit / clear.
  const [city, setCity] = useState<string>('');
  // City label for the HEADING only — tracks whatever city the
  // currently-DISPLAYED results actually came from, separate from the
  // live `city` filter state. `city` goes blank the instant the user
  // clears the location field, but the results themselves are left in
  // place (see the fetch effect below) — without this split, the
  // heading would flip to "All Restaurants" while still showing the
  // previous city's cards, which reads as wrong/inconsistent.
  const [displayCity, setDisplayCity] = useState<string>('');
  // Browser-resolved geolocation. Forwarded on every fetch so the
  // backend's distance score is non-zero. Always starts null — we
  // *first* ask the JS Geolocation API on mount, and only fall back
  // to localStorage if the live lookup fails / is denied.
  const [userCoords, setUserCoords] = useState<{ lat: number; lon: number } | null>(null);
  const observerTarget = useRef<HTMLDivElement>(null);

  // On mount, run the JS Geolocation API to pick up the user's current
  // position. On success → state + persist to localStorage as a fallback
  // for next time. On failure → silently restore the last-known coords
  // from localStorage if any, otherwise leave null (slot-count only ranking).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let cancelled = false;

    const restoreFromCache = () => {
      try {
        const lat = parseFloat(localStorage.getItem(USER_LAT_KEY) || '');
        const lon = parseFloat(localStorage.getItem(USER_LON_KEY) || '');
        if (Number.isFinite(lat) && Number.isFinite(lon) && !cancelled) {
          setUserCoords({ lat, lon });
        }
      } catch { /* localStorage disabled — ignore */ }
    };

    if (!navigator.geolocation) {
      restoreFromCache();
      return () => { cancelled = true; };
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return;
        const { latitude, longitude } = pos.coords;
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
          restoreFromCache();
          return;
        }
        try {
          localStorage.setItem(USER_LAT_KEY, String(latitude));
          localStorage.setItem(USER_LON_KEY, String(longitude));
        } catch { /* ignore */ }
        setUserCoords({ lat: latitude, lon: longitude });
      },
      () => {
        // Permission denied / position unavailable / timeout —
        // fall back to the last successfully-resolved coords.
        restoreFromCache();
      },
      { enableHighAccuracy: false, maximumAge: 5 * 60 * 1000, timeout: 8000 },
    );

    return () => { cancelled = true; };
  }, []);

  // Seed `city` from the cross-domain blob on mount + subscribe to
  // the hero's broadcast so filter changes propagate without going
  // through the URL. Event payload carries `{ city }` — an empty
  // string means the user cleared the field, so the list resets.
  useEffect(() => {
    // URL `?city=` wins on first load — it's what a shared/bookmarked
    // link (or the hero's own search submit, which now sets it per
    // BCP-1590) explicitly asked for. Falls back to the cross-domain
    // localStorage blob only when the URL carries no city at all, so
    // a bare `/restaurants` visit still restores the visitor's last
    // search.
    setCity(searchParams.get('city')?.trim() || readCityFromStorage());
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ city?: string }>).detail;
      setCity((detail?.city ?? '').trim());
    };
    window.addEventListener('bipRestaurantsCityChanged', handler);
    return () => window.removeEventListener('bipRestaurantsCityChanged', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Merge user coords into a params bag so every fetch carries them when known. */
  const withCoords = useCallback((params: Record<string, string>): Record<string, string> => {
    if (userCoords) {
      params.userLat = String(userCoords.lat);
      params.userLon = String(userCoords.lon);
    }
    return params;
  }, [userCoords]);

  useEffect(() => {
    if (initialData.data.length > 0) {
      setRestaurants(initialData.data);
      setPage(initialData.page);
      setHasMore(initialData.page < initialData.totalPages);
      setTotal(initialData.total);
      setIsLoading(false);
      return undefined;
    }
    // City no longer travels via the URL — sourced from `city` state
    // which the mount effect above seeded from
    // `theatre-searches-city` and refreshes via the
    // `bipRestaurantsCityChanged` event the hero broadcasts. Other
    // narrowing params (`restaurantId`, `q`) still ride the URL as
    // before, because they're deep-linkable / bookmarkable.
    const hasNarrowingQuery = !!(
      city
      || searchParams.get('restaurantId')
      || searchParams.get('q')
    );
    if (!hasNarrowingQuery) {
      // No city / restaurantId / q available — e.g. the user cleared
      // the location after a "Find a table" search. Deliberately leave
      // the currently-displayed results untouched instead of wiping
      // them: clearing one filter shouldn't blank the whole list the
      // user just searched for.
      return undefined;
    }
    const params: Record<string, string> = {};
    if (city) params.city = city;
    searchParams.forEach((v, k) => { params[k] = v; });
    setIsLoading(true);
    setSearchError(false);
    fetchRestaurants(withCoords(params))
      .then((result) => {
        setRestaurants(result.data);
        setPage(result.page);
        setHasMore(result.page < result.totalPages);
        setTotal(result.total);
        setDisplayCity(city);
      })
      .catch(() => setSearchError(true))
      .finally(() => setIsLoading(false));
    return undefined;
  }, [initialData, searchParams, withCoords, city, retryKey]);

  // When geolocation resolves *after* SSR, re-fetch page 1 with the
  // coords so distance scoring (and the resulting reorder) takes effect.
  // Skipped on the initial mount where SSR already returned data with
  // no coords — userCoords starts as null in that branch.
  const initialFetchDone = useRef(false);
  useEffect(() => {
    if (!userCoords) return;
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      return;
    }
    // Only re-fetch on geo-resolve when the URL actually narrows to
    // something — city / restaurantId / free-text search. Firing
    // against a bare `/restaurants` URL would pull the whole
    // catalogue sorted by distance, which is exactly the "all
    // restaurants without a city" behaviour the CMS spec forbids.
    // A subsequent city pick from the autocomplete flips the URL and
    // this effect re-runs cleanly on the next render.
    const hasNarrowingQuery = !!(
      city
      || searchParams.get('restaurantId')
      || searchParams.get('q')
    );
    if (!hasNarrowingQuery) return;
    const params: Record<string, string> = { page: '1', limit: '4' };
    if (city) params.city = city;
    searchParams.forEach((v, k) => { if (k !== 'page' && k !== 'limit') params[k] = v; });
    setIsLoading(true);
    fetchRestaurants(withCoords(params))
      .then((result) => {
        setRestaurants(result.data);
        setPage(result.page);
        setHasMore(result.page < result.totalPages);
        setTotal(result.total);
      })
      .catch(() => { /* keep existing list */ })
      .finally(() => setIsLoading(false));
  }, [userCoords, searchParams, withCoords, city]);

  const loadMore = useCallback(async () => {
    // Belt-and-braces: never fetch a "next page" when there's nothing
    // on screen to append to. Prevents an observer misfire during a
    // filter transition (where `hasMore` may briefly stay truthy from
    // a stale render) from wiping the empty state with a phantom
    // fetch. `hasMore` alone is not enough — it lags one render
    // behind `restaurants`.
    if (isLoading || !hasMore || restaurants.length === 0) return;
    setIsLoading(true);
    try {
      const nextPage = page + 1;
      const params: Record<string, string> = { page: String(nextPage), limit: '4' };
      if (city) params.city = city;
      searchParams.forEach((value, key) => { if (key !== 'page') params[key] = value; });
      const result = await fetchRestaurants(withCoords(params));
      setRestaurants((prev) => [...prev, ...result.data]);
      setPage(nextPage);
      setHasMore(nextPage < result.totalPages);
    } catch {
      // silent — let user scroll again to retry
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, hasMore, page, searchParams, withCoords, restaurants.length, city]);

  // Observer wires ONLY when there's a next page to load AND we
  // already have visible results. The sentinel div is
  // conditionally rendered on the same gate below — so with an
  // empty list, the target ref is null, we short-circuit here, and
  // the empty state stays stable (no phantom fetches). When the
  // user's filter change repopulates the list, the sentinel remounts
  // and this effect re-runs.
  const canPaginate = hasMore && restaurants.length > 0;
  useEffect(() => {
    if (!canPaginate) return;
    const target = observerTarget.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) loadMore(); },
      { threshold: 0.1 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [loadMore, canPaginate]);

  //const city = searchParams.get('city') || '';
  const headingLabel = displayCity ? `${displayCity} Restaurants` : 'All Restaurants';
  // Forward the user-picked date / party size to each card so the
  // slot strip reflects real-time availability for the right context.
  const dateParam = searchParams.get('date') || undefined;
  const peopleParam = (() => {
    const raw = searchParams.get('people');
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : undefined;
  })();
  const timeParam = searchParams.get('time') || undefined;

  return (
    <>
      {restaurants.length > 0 && (
        <h2 className="listing-heading mb-2">
          {headingLabel}{total > 0 ? `: ${total} found` : ''}
        </h2>
      )}

      {/* Empty states — mirrors Hotels' pattern (icon + heading +
          helper text, plus a retry CTA when the fetch itself failed
          rather than just returning zero matches). */}
      {restaurants.length === 0 && !isLoading && searchError && (
        <div className="text-center py-16">
          <i className="fas fa-exclamation-circle text-5xl text-orange mb-4 block" />
          <h3 className="text-lg font-bold text-secondary mb-2">Search failed</h3>
          <p className="text-info text-sm mb-4">The service may be starting up. Please try again.</p>
          <button
            type="button"
            onClick={() => setRetryKey((k) => k + 1)}
            className="bg-primary text-white text-sm font-bold px-6 py-2.5 rounded-xl"
          >
            Try Again
          </button>
        </div>
      )}
      {restaurants.length === 0 && !isLoading && !searchError && (
        <div className="text-center py-16">
          <i className="fas fa-utensils text-6xl text-info mb-4 block" />
          <h3 className="text-lg font-bold text-secondary mb-2">No restaurants found</h3>
          {/* <p className="text-info text-sm">Try adjusting your search or filters</p> */}
        </div>
      )}

      {/* Cards — 1/2/3/4-col grid, vertical restaurant tiles */}
      <div className="mt-3 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {restaurants.map((r) => (
          <RestaurantCard
            key={r._id}
            restaurant={r}
            date={dateParam}
            people={peopleParam}
            time={timeParam}
          />
        ))}

        {/* Infinite-scroll loader — skeleton cards matching the real
            RestaurantCard footprint so the list height doesn't jump
            when the next page resolves. */}
        {isLoading && (
          <>
            <RestaurantCardSkeleton />
            <RestaurantCardSkeleton />
            <RestaurantCardSkeleton />
            <RestaurantCardSkeleton />
          </>
        )}
      </div>

      {/* Sentinel — IntersectionObserver target. Mounted only when
          there IS a next page to load AND we already have cards on
          screen — an empty-state page never carries the sentinel,
          so infinite-scroll can't misfire against "no results". */}
      {canPaginate && (
        <div ref={observerTarget} className="py-6" aria-hidden />
      )}
    </>
  );
}
